<script setup lang="ts">
import { usePage } from '@inertiajs/vue3'
import { useChatSidebar } from '@/stores/useChatSidebar'
import { useChatStore } from '@/stores/useChatStore'
import AppChatSidebar from '@/components/AppChatSidebar.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { computed, defineAsyncComponent, onUnmounted } from 'vue'

const page = usePage()
const { isChatOpen, chatDrawerWidth, chatDrawerWidthMin, chatDrawerWidthMax } = useChatSidebar()
const chatStore = useChatStore()

function startResize(e: MouseEvent) {
    e.preventDefault()
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    function onMouseMove(e: MouseEvent) {
        const w = window.innerWidth - e.clientX
        const clamped = Math.min(chatDrawerWidthMax, Math.max(chatDrawerWidthMin, w))
        chatDrawerWidth.value = clamped
    }
    function onMouseUp() {
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
}

onUnmounted(() => {
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
})

// Show chat when open and we have a room (either on client page or opened from list)
const shouldShowChat = computed(() => {
    return isChatOpen.value && !!chatStore.roomId
})

const drawerStyle = computed(() => {
    if (!shouldShowChat.value) return {}
    return {
        width: `min(100vw, ${chatDrawerWidth.value}px)`,
    }
})

const ChatSidebar = defineAsyncComponent(() => import('@/components/AppChatSidebar.vue'))
</script>

<template>
    <transition name="slide-right">
        <div
            v-if="shouldShowChat"
            class="fixed inset-y-0 right-0 flex flex-col bg-white dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800 shadow-xl z-40 sm:min-w-[320px]"
            :style="drawerStyle"
        >
            <TooltipProvider :delay-duration="0">
            <!-- Resize-handtag till vänster om chatten: dra för att ändra bredd -->
            <Tooltip>
                <TooltipTrigger as-child>
                    <div
                        class="absolute left-0 top-0 bottom-0 w-3 z-50 cursor-col-resize flex items-center justify-center touch-none hidden sm:flex bg-zinc-100 dark:bg-zinc-800/80 hover:bg-primary/10 border-r border-zinc-200 dark:border-zinc-700"
                        aria-label="Dra för att ändra bredd på chatten"
                        role="separator"
                        @mousedown="startResize"
                    >
                        <span class="flex flex-col gap-1.5 pointer-events-none">
                            <span class="w-1 h-5 rounded-full bg-zinc-400 dark:bg-zinc-500 shrink-0" />
                            <span class="w-1 h-5 rounded-full bg-zinc-400 dark:bg-zinc-500 shrink-0" />
                            <span class="w-1 h-5 rounded-full bg-zinc-400 dark:bg-zinc-500 shrink-0" />
                        </span>
                    </div>
                </TooltipTrigger>
                <TooltipContent side="right" class="text-sm">
                    <p>Dra för att ändra bredd på chatten</p>
                </TooltipContent>
            </Tooltip>
            </TooltipProvider>
            <div class="flex-1 min-h-0 flex flex-col overflow-hidden min-w-0">
                <ChatSidebar />
            </div>
        </div>
    </transition>
</template>

<style scoped>
.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-right-enter-from,
.slide-right-leave-to {
    transform: translateX(100%);
    opacity: 0;
}
.slide-right-enter-to,
.slide-right-leave-from {
    transform: translateX(0);
    opacity: 1;
}
</style>
